import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import "./summaryScreen.css";
import RemainingBar from "../../common/remainingBar/remainingBar";
import gcseLogo from "../../assets/KSOL GCSE_logo.png";
import { Chart, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { useSharedState } from "../../context/formsContext";
import SummaryScreenLayout from "../../common/summaryScreenLayout/summaryScreenLayout";
import CustomButton from "../../common/customButton/customButton";
import { useNavigate, useLocation, useBlocker } from "react-router-dom";
import WordFromTutors from "../wordFromTutors/wordFromTutors";
import logo from "../../assets/ksolactipanel.png";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactGA from "react-ga4";
import { getProperty } from "../../utils/apiResponseHelper";

const SummaryScreen = () => {
  const {
    setIndex,
    index,
    summary,
    setSummary,
    setTestData,
    testData,

    prefix,
  } = useSharedState();

  const navigate = useNavigate();
  const location = useLocation();

  const [questionTitle, setQuestionTitle] = useState("");
  const [showTip, setShowTip] = useState(false);
  const [isGcse, setIsGcse] = useState(false);

  Chart.register(ArcElement, Tooltip, Legend);

  const toastId = useRef(null);

  useEffect(() => {
    const storedIsGcse = localStorage.getItem("isGcse");

    if (storedIsGcse === "true") {
      setIsGcse(true);
    } else {
      setIsGcse(false);
    }
  }, []);

  useEffect(() => {
    if (isGcse) {
      document.body.classList.add("gcse-mode");
    } else {
      document.body.classList.remove("gcse-mode");
    }
  }, [isGcse]);

  useEffect(() => {
    const handlePopState = () => {
      window.history.go(1);

      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.info(
          <div>
            <span
              style={{
                textDecoration: "underline",
                cursor: "pointer",
                color: "blue",
              }}
              onClick={() => navigate("/dashboard")}
            >
              Click here to return to dashboard
            </span>
          </div>,
          {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          }
        );
      }
    };

    window.history.pushState(null, null, location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    const sessionLoginToken = localStorage.getItem("loginToken");
    if (!sessionLoginToken && testData) {
      navigate("/loginScreen");
    }
  }, []);

  useEffect(() => {
    const quizIndex = localStorage.getItem("quizIndex");
    if (quizIndex) {
      setIndex(Number(quizIndex));
    }
    localStorage.setItem("previousPage", "/quizScreen");
  }, []);

  // Update question title when testData or index changes
  useEffect(() => {
    if (testData && Array.isArray(testData) && testData.length > 0 && testData[index]) {
      const testItem = testData[index];
      const quizTitle = getProperty(testItem, 'quizTitle') || getProperty(testItem, 'QuizTitle') || testItem?.QuizTitle || testItem?.quizTitle || '';
      setQuestionTitle(quizTitle);
    }
  }, [testData, index]);


  const fetchSummary = async () => {
    const sessionLoginToken = localStorage.getItem("loginToken");
    
    if (!sessionLoginToken) {
      return;
    }
    
    let sessionQuizSummaryId = localStorage.getItem("quizSummaryId");
    
    // Handle case where quizSummaryId might be stored as string "undefined" or "null"
    if (!sessionQuizSummaryId || sessionQuizSummaryId === "undefined" || sessionQuizSummaryId === "null") {
      return;
    }
    
    // Try to parse if it's JSON, otherwise use as is
    let parsedQuizSummaryId;
    try {
      parsedQuizSummaryId = JSON.parse(sessionQuizSummaryId);
    } catch (e) {
      parsedQuizSummaryId = sessionQuizSummaryId;
    }
    
    if (!prefix) {
      return;
    }

    try {
      const response = await fetch(
        `${prefix}/api40/Lesson/GetExplanationsSingleQuiz?QuizSummaryId=${parsedQuizSummaryId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionLoginToken}`,
            "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9"
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch explanations: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      
      if (data && Array.isArray(data) && data.length > 0) {
        setSummary(data);
        localStorage.setItem("summary", JSON.stringify(data));
      } else {
      }
    } catch (error) {
      
      try {
        await fetch(`${prefix}/Log/Log`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionLoginToken}`,
            "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9"
          },
          body: JSON.stringify({
            Message: `Error retrieving summary: ${error.message}`,
            Time: new Date().toISOString(),
          }),
        });
      } catch (logError) {
      }
    }
  };

  useEffect(() => {
    // Restore testData from localStorage
    const localTestData = localStorage.getItem("testData");
    if (localTestData) {
      try {
        const parsed = JSON.parse(localTestData);
        setTestData(parsed);
      } catch (e) {
      }
    }
    
    // Check if summary already exists in localStorage
    const localSummary = localStorage.getItem("summary");
    if (localSummary) {
      try {
        const parsed = JSON.parse(localSummary);
        if (parsed && Array.isArray(parsed) && parsed.length > 0) {
          setSummary(parsed);
        }
      } catch (e) {
      }
    }
    
    // Always fetch fresh summary data
    fetchSummary();
  }, [prefix]); // Add prefix as dependency since fetchSummary uses it

  const handleSetPieData = () => {
    // Default empty data structure for Chart.js
    const defaultData = {
      labels: ["Correct", "Passed", "Incorrect"],
      datasets: [
        {
          data: [0, 0, 0],
          backgroundColor: ["#9fd066", "#fdd03d", "#e74c3c"],
        },
      ],
    };
    
    if (summary && Array.isArray(summary) && summary.length > 0 && summary[index]) {
      const summaryItem = summary[index];
      const totalCorrect = getProperty(summaryItem, 'totalCorrect') || getProperty(summaryItem, 'TotalCorrect') || summaryItem?.TotalCorrect || summaryItem?.totalCorrect || 0;
      const totalPassed = getProperty(summaryItem, 'totalPassed') || getProperty(summaryItem, 'TotalPassed') || summaryItem?.TotalPassed || summaryItem?.totalPassed || 0;
      const totalIncorrect = getProperty(summaryItem, 'totalIncorrect') || getProperty(summaryItem, 'TotalIncorrect') || summaryItem?.TotalIncorrect || summaryItem?.totalIncorrect || 0;
      
      return {
        labels: ["Correct", "Passed", "Incorrect"],
        datasets: [
          {
            data: [totalCorrect, totalPassed, totalIncorrect],
            backgroundColor: ["#9fd066", "#fdd03d", "#e74c3c"],
          },
        ],
      };
    }
    
    // Return default data structure if summary doesn't exist
    return defaultData;
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
  };

  const takeToCourse = () => {
    ReactGA.event({
      category: "Recommended Product Click",
      action: "Click",
      label: "recommended_product_click",
    });

    window.open(
      testData && testData[0] ? `https://www.ksol.co.uk${getProperty(testData[0], 'recommendedProductUrl') || getProperty(testData[0], 'RecommendedProductUrl') || testData[0]?.RecommendedProductUrl || testData[0]?.recommendedProductUrl || ''}` : '',
      "_blank",
      "noopener noreferrer"
    );
  };

  const VIDEO_URL = "https://player.vimeo.com/video/960292381?share=copy";
  const VIDEO_URL_GCSE = "https://player.vimeo.com/video/1064248255";

  const COLORS = ["#9fd066", "#fdd03d", "#e74c3c"];

  return (
    <>
      <Helmet>
        <title>Quiz Summary | KSOL</title>
        <meta name="description" content="View your test results and detailed explanations for each question." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div
        className={`flex justify-center w-full ${
          isGcse ? "bg-image-gcse" : "bg-image"
        }`}
      >
      <div className="grid grid-cols-12 gap-4 p-4 xl:mx-auto xl:w-4/5">
        <div className="col-span-12 xl:col-span-8 gap-4">
          <div className="flex flex-col-reverse xl:flex xl:flex-row justify-between items-center w-full">
            <div className="flex items-center flex-nowrap w-auto">
              <h2
                className={
                  isGcse
                    ? "text-left whitespace-nowrap text-white"
                    : "text-left whitespace-nowrap"
                }
              >
                Quiz Summary
              </h2>
              <div className="ml-4">
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  size="2x"
                  style={{
                    color: "blue",
                    backgroundColor: "white",
                    borderRadius: "50px",
                  }}
                  onClick={() => setShowTip((prevState) => !prevState)}
                />
              </div>
            </div>

            <div className="flex justify-center xl:justify-end w-auto">
              <img
                src={isGcse ? gcseLogo : logo}
                className={
                  isGcse ? "w-2/4  mb-4" : "w-2/4 md:w-3/12  xl:w-3/12 mb-4"
                }
              />
            </div>
          </div>

          {showTip && (
            <div>
              <WordFromTutors />
            </div>
          )}
          <div className="flex justify-center mt-4 mb-4 xl:justify-end xl:mt-0 xl:mb-0">
            <CustomButton
              type={"logOut"}
              text="Back to dashboard"
              onClick={() => navigate("/dashboard")}
              isGcse={isGcse}
            />
          </div>
          <div
            className={`col-span-10 col-start-2 mt-4 bg-gray-100  ${
              isGcse ? "rounded-xl" : "rounded-3xl"
            } shadow-md`}
          >
            <div
              className={`table-first-row p-4 ${
                isGcse ? "rounded-xl" : "rounded-3xl"
              }`}
            >
              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-3">
                  <h4 className="font-bold">
                    Quiz:{" "}
                    {testData && Array.isArray(testData) && testData.length > 0 && testData[index] ? (
                      <span style={{ color: "#fe5e52" }}>
                        {getProperty(testData[index], 'quizTitle') || getProperty(testData[index], 'QuizTitle') || testData[index]?.QuizTitle || testData[index]?.quizTitle || 'Loading...'}
                      </span>
                    ) : (
                      <span style={{ color: "#fe5e52" }}>Loading...</span>
                    )}
                  </h4>
                </div>
                <div className="col-span-3">
                  <h4 className="font-bold">
                    Total Questions:{" "}
                    <span
                      style={
                        isGcse ? { color: "#667fff" } : { color: "#fd943d" }
                      }
                    >
                      {summary && Array.isArray(summary) && summary.length > 0 && summary[0] ? (getProperty(summary[0], 'totalQuestions') || getProperty(summary[0], 'TotalQuestions') || summary[0]?.TotalQuestions || summary[0]?.totalQuestions || 0) : 0}
                    </span>
                  </h4>
                </div>
              </div>
            </div>

            {/* White background section */}
            <div className=" table-second-row p-4 ">
              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-3">
                  <h4 className="font-bold">
                    Questions Attempted:{" "}
                    <span
                      style={
                        isGcse ? { color: "#667fff" } : { color: "#fd943d" }
                      }
                    >
                      {summary && summary[0] ? (getProperty(summary[0], 'totalAttempted') || getProperty(summary[0], 'TotalAttempted') || summary[0]?.TotalAttempted || summary[0]?.totalAttempted || 0) : 0}
                    </span>
                  </h4>
                </div>
                <div className="col-span-3">
                  <h4 className="font-bold">
                    Total Questions Correct:{" "}
                    <span
                      style={
                        isGcse ? { color: "#667fff" } : { color: "#fd943d" }
                      }
                    >
                      {summary && summary[0] ? (getProperty(summary[0], 'totalCorrect') || getProperty(summary[0], 'TotalCorrect') || summary[0]?.TotalCorrect || summary[0]?.totalCorrect || 0) : 0}
                    </span>
                  </h4>
                </div>
              </div>
            </div>
          </div>
          <div className="py-4 flex items-start">
            <div className="blue-circle  mr-2"></div>
            <p className={isGcse ? "mr-2 text-white" : "mr-2"}>Your Answer</p>
            <div className="green-circle mr-2"></div>
            <p className={isGcse ? "mr-2 text-white" : "mr-2"}>
              Correct Answer
            </p>
            <div className="passed-circle  mr-2"></div>
            <p className={isGcse ? " text-white" : ""}>Question Passed</p>
          </div>
          <div className="col-span-4  xl:hidden">
            <div className="mt-4 xl:mt-0">
              <div className="flex flex-col space-y-8">
                <div
                  className={`bg-white p-4 ${
                    isGcse ? "rounded-xl" : "rounded-3xl"
                  } shadow-md`}
                >
                  <h3 className="text-center font-bold mb-4">
                    {summary && summary[index] ? (getProperty(summary[index], 'overallPct') || getProperty(summary[index], 'OverallPct') || summary[index]?.OverallPct || summary[index]?.overallPct || 0) : 0}% Correct
                  </h3>
                  <div className="w-full h-64">
                    <Pie
                      data={handleSetPieData()}
                      options={pieOptions}
                      className="w-40"
                    />
                  </div>
                  <div className="flex flex-wrap justify-center xl:justify-around mt-4">
                    <div className="flex items-center ">
                      <div className="green-circle "></div>
                      <p className="px-2 font-bold">Correct</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="red-circle"></div>
                      <p className="px-2 font-bold">Incorrect</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="yellow-circle"></div>
                      <p className="px-2 font-bold">Passed</p>
                    </div>
                  </div>
                </div>

                {/* <div
                  className={`bg-white p-4 ${
                    isGcse ? "rounded-xl" : "rounded-3xl"
                  } shadow-md`}
                >
                  <div className="relative pb-[56.25%] h-0">
                    <iframe
                      className="absolute top-0 left-0 w-full h-full rounded-3xl"
                      title="vimeo-player"
                      src={isGcse ? VIDEO_URL_GCSE : VIDEO_URL}
                      frameBorder="0"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div> */}
                {testData && testData[0] && (getProperty(testData[0], 'recommendedProduct') || getProperty(testData[0], 'RecommendedProduct') || testData[0]?.RecommendedProduct || testData[0]?.recommendedProduct) && (
                  <div
                    className={` mt-4 mb-4 flex flex-col items-center justify-center col-start-2 col-span-11 row-start-8 row-span-3 ${
                      isGcse ? "gcse-course-bg" : "course-bg"
                    } shadow-md xl:flex `}
                  >
                    <h3
                      className={
                        isGcse
                          ? `mt-4 text-center text-white font-bold p-4`
                          : `mt-4 text-center  font-bold p-4`
                      }
                    >
                      {testData && testData[0] ? (getProperty(testData[0], 'recommendedProduct') || getProperty(testData[0], 'RecommendedProduct') || testData[0]?.RecommendedProduct || testData[0]?.recommendedProduct || '')
                        : "Benefits of KSOL"}
                    </h3>

                    <div className="mb-4 lg:mt-8">
                      <CustomButton
                        text={`Start Now for *£${testData && testData[0] ? (getProperty(testData[0], 'recommendedCourseAmount') || getProperty(testData[0], 'RecommendedCourseAmount') || testData[0]?.RecommendedCourseAmount || testData[0]?.recommendedCourseAmount || '') : ''}`}
                        onClick={() => takeToCourse()}
                        isGcse={isGcse}
                      />
                    </div>
                    <div className="flex justify-center w-10/12 mb-4 text-center">
                      <a href="https://www.ksol.co.uk/contents/help/terms-and-conditions-for-purchases">
                        <p
                          className={` ${
                            isGcse ? "text-white" : "text-cyan-700"
                          } underline`}
                        >
                          *Terms and Conditions
                        </p>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {summary && Array.isArray(summary) && summary.length > 0 ? summary.map((summaryData, idx) => {
            const questionId = getProperty(summaryData, 'questionId') || getProperty(summaryData, 'QuestionId') || summaryData?.QuestionId || summaryData?.questionId || `question-${idx}`;
            const questionNumber = getProperty(summaryData, 'questionNumber') || getProperty(summaryData, 'QuestionNumber') || summaryData?.QuestionNumber || summaryData?.questionNumber || 0;
            const totalQuestions = getProperty(summaryData, 'totalQuestions') || getProperty(summaryData, 'TotalQuestions') || summaryData?.TotalQuestions || summaryData?.totalQuestions || 0;
            const totalCorrect = getProperty(summaryData, 'totalCorrect') || getProperty(summaryData, 'TotalCorrect') || summaryData?.TotalCorrect || summaryData?.totalCorrect || 0;
            const correctInd = getProperty(summaryData, 'correctInd') || getProperty(summaryData, 'CorrectInd') || summaryData?.CorrectInd || summaryData?.correctInd || '';
            const totalAttempted = getProperty(summaryData, 'totalAttempted') || getProperty(summaryData, 'TotalAttempted') || summaryData?.TotalAttempted || summaryData?.totalAttempted || 0;
            const explanation = getProperty(summaryData, 'explanation') || getProperty(summaryData, 'Explanation') || summaryData?.Explanation || summaryData?.explanation || '';
            const curriculumId = getProperty(summaryData, 'curriculumId') || getProperty(summaryData, 'CurriculumId') || summaryData?.CurriculumId || summaryData?.curriculumId || '';
            const questionText = getProperty(summaryData, 'questionText') || getProperty(summaryData, 'QuestionText') || summaryData?.QuestionText || summaryData?.questionText || '';
            const graphicLocation = getProperty(summaryData, 'graphicLocation') || getProperty(summaryData, 'GraphicLocation') || summaryData?.GraphicLocation || summaryData?.graphicLocation || '';
            const questionAttempted = getProperty(summaryData, 'questionAttempted') || getProperty(summaryData, 'QuestionAttempted') || summaryData?.QuestionAttempted || summaryData?.questionAttempted || '';
            
            return (
              <div key={questionId} className="col-span-10 col-start-2">
                <SummaryScreenLayout
                  questionNumber={questionNumber}
                  totalQuestions={totalQuestions}
                  questionsCorrect={totalCorrect}
                  questionAnsweredCorrectly={correctInd}
                  questionsAttempted={totalAttempted}
                  explanation={explanation}
                  questionId={questionId}
                  curriculumId={curriculumId}
                  questionText={questionText}
                  questionImages={graphicLocation}
                  isPassed={questionAttempted}
                  isGcse={isGcse}
                />
              </div>
            );
          }) : (
            <div className="col-span-10 col-start-2 p-4">
              <p className="text-center">Loading summary data...</p>
            </div>
          )}
          <div className="flex justify-center mt-4 mb-4 xl:justify-end xl:mt-0 xl:mb-0">
            <CustomButton
              type={"logOut"}
              text="Back to dashboard"
              onClick={() => navigate("/dashboard")}
              isGcse={isGcse}
            />
          </div>
        </div>

        {/* Right side: Pie chart and video */}
        <div className="hidden xl:block col-span-4 h-full">
          <div className="sticky top-4 h-view">
            <div className="flex flex-col space-y-8">
              <div
                className={`bg-white p-4 ${
                  isGcse ? "rounded-xl" : "rounded-3xl"
                } shadow-md`}
              >
                <h3 className="text-center font-bold mb-4">
                  {" "}
                  {summary && summary[index] ? (getProperty(summary[index], 'overallPct') || getProperty(summary[index], 'OverallPct') || summary[index]?.OverallPct || summary[index]?.overallPct || 0) : 0}% Correct
                </h3>
                <div className="w-full h-64">
                  <Pie data={handleSetPieData()} options={pieOptions} />
                </div>
                <div className="flex justify-around mt-4">
                  <div className="flex items-center space-x-2">
                    <div className="green-circle"></div>
                    <p className="font-bold">Correct</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="red-circle"></div>
                    <p className="font-bold">Incorrect</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="yellow-circle"></div>
                    <p className="font-bold">Passed</p>
                  </div>
                </div>
              </div>
              {testData && testData[0] && (getProperty(testData[0], 'recommendedProduct') || getProperty(testData[0], 'RecommendedProduct') || testData[0]?.RecommendedProduct || testData[0]?.recommendedProduct) && (
                <div
                  className={`hidden mt-4 flex flex-col items-center justify-center col-start-2 col-span-11 row-start-8 row-span-3 ${
                    isGcse ? "gcse-course-bg" : "course-bg"
                  } shadow-md xl:flex`}
                >
                  <h3
                    className={
                      isGcse
                        ? "font-bold text-center p-2 text-white"
                        : "font-bold text-center p-2"
                    }
                  >
                    {testData && testData[0] ? (getProperty(testData[0], 'recommendedProduct') || getProperty(testData[0], 'RecommendedProduct') || testData[0]?.RecommendedProduct || testData[0]?.recommendedProduct || '') : ''}
                  </h3>

                  <div className="mb-4 mt-8">
                    <CustomButton
                      text={"Enrol Now!"}
                      onClick={() => takeToCourse()}
                      isGcse={isGcse}
                    />
                  </div>
                </div>
              )}

              {/* <div
                className={`bg-white p-4 ${
                  isGcse ? "rounded-xl" : "rounded-3xl"
                } shadow-md`}
              >
                <div className="relative pb-[56.25%] h-0">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full rounded-3xl"
                    title="vimeo-player"
                    src={VIDEO_URL}
                    frameBorder="0"
                    allowFullScreen
                  ></iframe>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default SummaryScreen;
